'use client';

import Script from 'next/script';
import { FC, useEffect, useRef, useState } from 'react';

export interface SearchIFrameProps {
  src: string;
  title: string;
}

export const SearchIFrame: FC<SearchIFrameProps> = ({
  src,
  title,
  ...props
}: SearchIFrameProps) => {
  const [url, setUrl] = useState<string>();
  const [loaded, setLoaded] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const baseUrl =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://www.o2.co.uk';

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const verticalUrl = searchParams.get('verticalUrl');
    const referrerPageUrl = searchParams.get('referrerPageUrl');

    let referrer = referrerPageUrl || null;

    if (!referrer || referrer === baseUrl || referrer === `${baseUrl}/`) {
      const docReferrer = document.referrer;
      if (docReferrer && docReferrer.startsWith(baseUrl)) {
        referrer = docReferrer;
      }
    }

    let srcBaseUrl = src;
    let srcSearchParams = new URLSearchParams();

    try {
      const srcUrl = new URL(src);
      srcBaseUrl = `${srcUrl.protocol}//${srcUrl.host}${srcUrl.pathname}`;
      srcSearchParams = new URLSearchParams(srcUrl.search);
    } catch {
      const srcParts = src.split('?');
      srcBaseUrl = srcParts[0];
      if (srcParts[1]) {
        srcSearchParams = new URLSearchParams(srcParts[1]);
      }
    }

    const iframeParams = new URLSearchParams(srcSearchParams.toString());

    searchParams.forEach((value, key) => {
      if (key !== 'verticalUrl') {
        iframeParams.set(key, value);
      }
    });

    if (referrer) {
      iframeParams.set('referrerPageUrl', referrer);
    }

    const search = iframeParams.toString();
    const finalUrl = `${srcBaseUrl}${verticalUrl ? `/${verticalUrl}` : ''}${search ? `?${search}` : ''}`;
    
    setUrl(finalUrl);

    const fallbackTimer = setTimeout(() => {
      setLoaded(true);
    }, 3000);

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [src, baseUrl]);

  const onLoad = () => {
    setLoaded(true);
  };

  return (
    <div style={{ display: loaded ? 'block' : 'none' }}>
      {url && (
        <iframe
          ref={iframeRef}
          id='embededIframe'
          title={title}
          onLoad={onLoad}
          src={url}
          width='100%'
          allow='fullscreen'
          {...props}
        ></iframe>
      )}
      {!url && (
        <div style={{ padding: '20px', background: '#f0f0f0' }}>
          <p>Loading iframe...</p>
        </div>
      )}
      <Script
        type='text/javascript'
        src='/scripts/zepto/iframe-resizer.min.js'
        onReady={() => {
          try {
            const iframeElement = iframeRef.current;
            if (!iframeElement) return;

            window.iFrameResize(
              {
                checkOrigin: false,
                onMessage: (event: Record<'message', string>) => {
                  try {
                    const message = JSON.parse(event.message);

                    if (message['params']) {
                      const params = new URLSearchParams(message['params']);
                      const path = `${window.location.pathname}?${params}`;
                      history.replaceState(null, '', path);
                    }
                  } catch {
                    // NOSONAR - Ignore parsing errors from iframe messages
                  }
                },
                onResized: () => {},
              },
              '#embededIframe'
            );
          } catch {
            // NOSONAR - Silently fail if iframe resizer initialization fails
          }
        }}
      />
    </div>
  );
};
